import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { useState, useEffect } from "react";
import { Dialog } from "@mui/material";
import { useDialog } from "app/hook";
import { DialogType } from "app/types";
import { AppDispatch } from "app/store";
import { useScale, usePlaySound } from "common/utils";
import { ParentCloseBtn, FadeTransition } from "common/elements";
import I18n from "features/parent/i18n";
import { updateUser } from "features/user/api";

interface GenderEditModalProps {
  open: boolean;
  currentGender: string;
  uid: string;
  onClose: () => void;
}

type GenderOption = "male" | "female";

const GENDER_OPTIONS = [
  {
    key: "male" as const,
    labelKey: "MSG_GENDER_MALE_BY_PARENT",
    icon: "boy",
    bgColor: "bg-blue-100",
  },
  {
    key: "female" as const,
    labelKey: "MSG_GENDER_FEMALE_BY_PARENT",
    icon: "girl",
    bgColor: "bg-red-100",
  },
];

export const GenderEditModal = ({ open, currentGender, uid, onClose }: GenderEditModalProps) => {
  const play = usePlaySound();
  const { scale } = useScale();
  const handleDialog = useDialog();
  const dispatch = useDispatch<AppDispatch>();
  const [gender, setGender] = useState<GenderOption | null>(
    currentGender === "male" || currentGender === "female" ? currentGender : null,
  );
  const [disable, setDisable] = useState(false);

  useEffect(() => {
    if (open) {
      setGender(currentGender === "male" || currentGender === "female" ? currentGender : null);
      setDisable(false);
    }
  }, [open, currentGender]);

  const handleClick = () => {
    if (!disable) {
      setDisable(true);
      play();

      handleDialog({ type: DialogType.LOADING, value: true });

      dispatch(
        updateUser({
          uid,
          userInfo: { gender: gender ?? "" },
        }),
      )
        .unwrap()
        .then(() => {
          toast(
            <div>
              <p className="text text-textcolor-black">{I18n.t("MSG_TOAST_EDIT_SUCCESS")}</p>
            </div>,
          );
          onClose();
        })
        .catch((error) => {
          console.error(error);
          setDisable(false);
        })
        .finally(() => handleDialog({ type: DialogType.LOADING, value: false }));
    }
  };

  return (
    <Dialog
      fullScreen
      open={open}
      maxWidth={false}
      sx={{ zIndex: 99999 }}
      TransitionComponent={FadeTransition}
      PaperProps={{ style: { backgroundColor: "transparent" } }}
    >
      <div className="flex-col-center w-full h-full bg-textcolor-black/50">
        <div style={{ transform: `scale(${scale})` }}>
          <div className="flex-col-el w-[560px] border-solid border-gray2-30 border-[16px] rounded-[16px] bg-gray2-30">
            <div className="flex-col-el justify-start w-full h-full relative p-6 rounded-[8px] bg-white">
              <ParentCloseBtn close={onClose} />
              <div className="flex-row-el items-center gap-2 mb-4 pb-3 border-b border-outline shrink-0">
                <span className="material-symbols-outlined !text-[54px] text-red-100">wc</span>
                <h2 className="text-[33px] text-textcolor-black">
                  {I18n.t("MSG_PARENT_PAGE_GENDER_EDIT_TITLE")}
                </h2>
              </div>
              <div className="flex-col-view items-center py-4">
                <div className="flex-row-el flex-center gap-6 my-6">
                  {GENDER_OPTIONS.map((option) => (
                    <div
                      key={option.key}
                      onClick={() => {
                        play();
                        setGender(option.key);
                      }}
                      className={`flex-col-el items-center justify-center w-[200px] h-[200px] cursor-pointer select-none ${
                        gender === option.key ? option.bgColor : "bg-gray2-60"
                      }`}
                    >
                      <span className="material-symbols-outlined !text-6xl text-white mb-3">
                        {option.icon}
                      </span>
                      <p className="text-body text-[24px] text-white text-center">
                        {I18n.t(option.labelKey)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex-row-el flex-center gap-4">
                <button
                  onClick={onClose}
                  className="flex-row-el items-center justify-center w-[160px] py-3 bg-white border border-gray2-60 text-textcolor-black rounded-[12px] text-[24px] cursor-pointer hover:bg-gray2-20 transition-all"
                >
                  {I18n.t("MSG_CANCEL_BTN_BY_PARENT")}
                </button>
                <button
                  onClick={handleClick}
                  disabled={disable || !gender}
                  className={`flex-row-el items-center justify-center w-[160px] py-3 bg-ink text-white border-none rounded-[12px] text-[24px] transition-all ${
                    disable || !gender
                      ? "opacity-50 cursor-not-allowed"
                      : "cursor-pointer hover:opacity-90"
                  }`}
                >
                  {I18n.t("MSG_CONFIRM_BTN_BY_PARENT")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
